/**
 * driverFatigueTick — pure helper extracted from coreSlice.nextDay
 * (03/06/2026 round 3 refactor of the driver block).
 *
 * Handles in a single pass:
 *   • Auto-return from training / vacation / sick / striking when their
 *     respective `*Until` day is reached.
 *   • Fatigue accumulation on working days (+8 × fatigueMultiplier,
 *     veteran-depot discount) and recovery on rest days (−25).
 *   • Satisfaction drift (training-end bonus, office passive boost,
 *     trait-based daily delta, resting bonus).
 *   • Alerts on burnout (>80 fatigue) and discontent (<40 satisfaction).
 *
 * The helper does NOT push messagerie entries itself (would require the
 * gameStore `get`). Instead, it returns `sickReturnDriverIds` so the
 * orchestrator can fire `drv-illness-return-01` for each one.
 *
 * @param {Object} state - the current game state snapshot
 * @param {boolean} working - true when isWorkingDay(state.day)
 * @param {Object} opts
 * @param {number} opts.fatigueMultiplier - 0..1 multiplier from veteran depot
 *   (1 = base, 0.85 with depot #3 acquired)
 * @param {number} opts.officeSatisfactionBoost - daily satisfaction bump
 *   from office upgrades (0 if none)
 * @returns {{
 *   updatedDrivers: Array,
 *   notifications: Array,
 *   alerts: Array,
 *   sickReturnDriverIds: Array<{id: string, name: string}>,
 * }}
 */
import { generateId } from '../helpers';
import { getTraitDailyDelta } from '../../data/personalityTraits';
import { lumeryDuoActive, LUMERY_BROTHER_IDS, LUMERY_DUO_FATIGUE_FACTOR } from '../../data/drivers';

export function runDriverFatigueTick(state, working, opts = {}) {
  const fatigueMultiplier = opts.fatigueMultiplier ?? 1;
  const officeSatisfactionBoost = opts.officeSatisfactionBoost || 0;
  // 14/06/2026 — bonus fratrie Lumery : −10 % d'accumulation de fatigue
  // pour chacun des deux frères quand ils sont au même dépôt.
  const lumeryDuo = lumeryDuoActive(state.drivers);

  const notifications = [];
  const alerts = [];
  const sickReturnDriverIds = [];
  // 04/06/2026 — collected for inbox emails (user request: "Il faudrait
  // un email pour indiquer que la formation est terminée").
  const trainingCompletedDrivers = [];
  // 08/02/2026 — Q2 bug Sébastien Leclerc : assignments to re-establish
  // when a driver auto-returns from vacation (preVacationVehicleId set
  // when the vacation was granted in messageSlice).
  const vacationResumeAssignments = [];

  const updatedDrivers = state.drivers.map(d => {
    let fatigue = d.fatigue;
    let satisfaction = d.satisfaction;
    let status = d.status;

    // Auto-return from training.
    // 26/06/2026 hotfix bug user — la durée annoncée doit correspondre à
    // la durée d'indispo réelle. Avant : libération à `totalDays >=
    // trainingEndDay`, ce qui ajoutait 1 jour (le jour d'enroll comptait
    // en double car le driver passait en 'training' immédiatement). Désor-
    // mais : libération au tick qui fait advancer le compteur ATTEINDRE
    // trainingEndDay, ce qui donne pile `days` jours d'indispo.
    if (d.status === 'training' && d.trainingEndDay && (state.totalDays + 1) >= d.trainingEndDay) {
      status = 'idle';
      satisfaction = Math.min(100, satisfaction + 5);
      notifications.push({
        id: generateId(),
        type: 'training_complete',
        title: 'Formation terminée',
        message: `${d.name} a terminé sa formation et est de nouveau disponible.`,
        day: state.totalDays + 1,
        read: false,
      });
      // 04/06/2026 — also flag for an inbox email push by the orchestrator
      trainingCompletedDrivers.push({
        id: d.id,
        name: d.name,
        trainingType: d.trainingType || null,
      });
    }

    // Auto-return from vacation.
    if (d.status === 'vacation' && d.vacationUntil && state.totalDays >= d.vacationUntil) {
      status = 'idle';
      satisfaction = Math.min(100, satisfaction + 4);
      notifications.push({
        id: generateId(),
        type: 'vacation_return',
        title: 'Retour de congés',
        message: `${d.name} est de retour de vacances, reposé(e) et disponible.`,
        day: state.totalDays + 1,
        read: false,
      });
      // 08/02/2026 — Q2 bug Sébastien Leclerc : if the driver had a vehicle
      // + contract before vacation, schedule a re-assignment so they
      // resume their mission instead of becoming "available".
      if (d.preVacationVehicleId) {
        vacationResumeAssignments.push({
          driverId: d.id,
          driverName: d.name,
          vehicleId: d.preVacationVehicleId,
          contractId: d.preVacationContractId || null,
          subcontract: !!d.preVacationSubcontract,
        });
      }
    }

    // Auto-return from sick leave.
    if (d.status === 'sick' && d.sickUntil && state.totalDays >= d.sickUntil) {
      // 18/06/2026 — si l'arrêt était couvert par un intérimaire
      // (evt-rh-02 choix 0 : véhicule conservé + mission toujours active),
      // le chauffeur reprend directement sa mission ('active').
      const linkedVehicle = d.assignedVehicleId
        ? (state.vehicles || []).find(v => v.id === d.assignedVehicleId)
        : null;
      const resumesMission = !!(linkedVehicle && linkedVehicle.status === 'active'
        && linkedVehicle.assignedDriverId === d.id);
      status = resumesMission ? 'active' : 'idle';
      notifications.push({
        id: generateId(),
        type: 'sick_return',
        title: 'Retour d\'arrêt maladie',
        message: resumesMission
          ? `${d.name} reprend le travail après son arrêt maladie et retrouve sa mission (fin de l'intérim).`
          : `${d.name} reprend le travail après son arrêt maladie.`,
        day: state.totalDays + 1,
        read: false,
      });
      sickReturnDriverIds.push({ id: d.id, name: d.name, resumeVehicleId: resumesMission ? linkedVehicle.id : null });
    }

    // Auto-return from strike.
    if (d.status === 'striking' && d.strikingUntil && state.totalDays >= d.strikingUntil) {
      status = 'idle';
      notifications.push({
        id: generateId(),
        type: 'strike_return',
        title: 'Reprise du travail',
        message: `${d.name} a mis fin à sa grève et reprend ses missions.`,
        day: state.totalDays + 1,
        read: false,
      });
    }

    // Active working day → accumulate fatigue + bonuses.
    if (working && (status === 'active' || d.status === 'active')) {
      const lumeryFactor = (lumeryDuo && LUMERY_BROTHER_IDS.includes(d.dataId))
        ? LUMERY_DUO_FATIGUE_FACTOR : 1;
      fatigue = Math.min(100, fatigue + (8 * fatigueMultiplier * lumeryFactor));
      if (officeSatisfactionBoost > 0) {
        satisfaction = Math.min(100, satisfaction + (officeSatisfactionBoost * 0.15));
      }
      if (d.assignedVehicleId) satisfaction = Math.min(100, satisfaction + 0.2);
      // Trait-based daily delta — applies on top of the +0.2 baseline.
      const linkedVehicle = state.vehicles.find(v => v.id === d.assignedVehicleId);
      const linkedContract = state.activeContracts.find(c => c.driverId === d.id);
      const traitDelta = getTraitDailyDelta(d, linkedVehicle, linkedContract);
      if (traitDelta !== 0) {
        satisfaction = Math.max(0, Math.min(100, satisfaction + traitDelta));
      }
    } else if (status !== 'training') {
      // Rest day (or non-working day) → recover 25 fatigue.
      fatigue = Math.max(0, fatigue - 25);
    }

    // Resting status → additional recovery (−25 fatigue + sat bump + auto-flip to idle).
    if (d.status === 'resting') {
      fatigue = Math.max(0, fatigue - 25);
      satisfaction = Math.min(100, satisfaction + 2);
      if (fatigue <= 10) status = 'idle';
    }

    // Burnout / dissatisfaction alerts.
    if (fatigue > 80) {
      satisfaction = Math.max(0, satisfaction - 2);
      alerts.push({ type: 'fatigue', message: `${d.name} est épuisé (fatigue: ${Math.round(fatigue)}%)`, severity: 'grave' });
    }
    if (satisfaction < 40) {
      alerts.push({ type: 'satisfaction', message: `${d.name} est mécontent (satisfaction: ${Math.round(satisfaction)}%)`, severity: 'modéré' });
    }

    return {
      ...d,
      fatigue: Math.round(fatigue * 10) / 10,
      satisfaction: Math.round(satisfaction * 10) / 10,
      // Preserve in-progress training/vacation/sick states; only the
      // auto-return branches above flip status to 'idle'.
      status: (d.status === 'training' && (state.totalDays + 1) < (d.trainingEndDay || 0)) ? 'training'
            : (d.status === 'vacation' && state.totalDays < (d.vacationUntil || 0)) ? 'vacation'
            : (d.status === 'sick' && state.totalDays < (d.sickUntil || 0)) ? 'sick'
            : status,
      // 08/02/2026 — When vacation just ended AND we registered a resume,
      // restore the vehicle assignment directly on the driver object so
      // tickVehicles / pay flows pick it up the next tick. The orchestrator
      // also fixes the vehicle.assignedDriverId in coreSlice.nextDay.
      ...(d.status === 'vacation' && d.vacationUntil && state.totalDays >= d.vacationUntil && d.preVacationVehicleId
        ? {
            assignedVehicleId: d.preVacationVehicleId,
            preVacationVehicleId: null,
            preVacationContractId: null,
            preVacationSubcontract: null,
          }
        : {}),
      // 22/07/2026 — bug user prod « boucle formations » : nettoyer les
      // champs de formation à la sortie, sinon trainingEndDay/trainingType
      // périmés restent sur le chauffeur pour toujours.
      ...(d.status === 'training' && d.trainingEndDay && (state.totalDays + 1) >= d.trainingEndDay
        ? { trainingEndDay: null, trainingType: null }
        : {}),
    };
  });

  return { updatedDrivers, notifications, alerts, sickReturnDriverIds, trainingCompletedDrivers, vacationResumeAssignments };
}
